import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Clock, Plus } from 'lucide-react';
import { LEVEL_LABELS, MatchedModule } from '../../types/pathway';
import { formatHours, matchBand } from '../../utils/pathwayMatching';
interface ModuleCardProps {
  match: MatchedModule;
  inPathway: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onTogglePathway: () => void;
}
const bandStyles: Record<string, string> = {
  Strong: 'border-[#05634B] text-[#007558]',
  Good: 'border-[#5290A6] text-[#30718D]',
  Partial: 'border-[#828282] text-[#6B7280]'
};
export function ModuleCard({
  match,
  inPathway,
  expanded,
  onToggleExpand,
  onTogglePathway
}: ModuleCardProps) {
  const {
    module,
    score,
    matchedTerms
  } = match;
  const band = score !== null ? matchBand(score) : null;
  const detailsId = `module-details-${module.id}`;
  return <article className={`bg-white rounded-xl shadow-sm border transition-colors ${inPathway ? 'border-[#30718D]' : 'border-gray-100'}`} data-id="element-1551" data-mp="3nl0es68045fw">
      <div className="p-5" data-id="element-1552" data-mp="38e0xnpywalsp">
        <div className="flex items-start gap-4" data-id="element-1553" data-mp="33fr1pd5cjh61">
          <button type="button" onClick={onTogglePathway} aria-pressed={inPathway} aria-label={inPathway ? `Remove ${module.name} from pathway` : `Add ${module.name} to pathway`} className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] ${inPathway ? 'border-[#30718D] bg-[#30718D] text-white hover:bg-[#265a70]' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-1554" data-mp="33h25hcyskox5">
            {inPathway ? <Check size={16} data-id="element-1555" data-mp="dy9xuu14xxpb" /> : <Plus size={16} data-id="element-1556" data-mp="dwpbhroz5l2j" />}
          </button>

          <div className="min-w-0 flex-1" data-id="element-1557" data-mp="8zfg7vewmf6s">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-1558" data-mp="8xm6g78zveuk">
              <h3 className="text-base font-bold text-gray-900" data-id="element-1559" data-mp="23echw6596k7c">
                {module.name}
              </h3>
              <span className="text-sm text-gray-500" data-id="element-1560" data-mp="287ymrc56djsl">{module.category}</span>
            </div>

            <p className="mt-1.5 text-sm text-gray-600" data-id="element-1561" data-mp="1tjklh5neq3e5">{module.description}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1562" data-mp="1yhm4jqku22pm">
              {band && <span className={`inline-flex h-6 items-center justify-center rounded border bg-white px-3 text-xs font-bold whitespace-nowrap ${bandStyles[band]}`} data-id="element-1563" data-mp="2m7q6zumabwf1">
                  {band} match
                </span>}
              <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-1564" data-mp="2h6sofgxd50d0">
                <Clock size={13} aria-hidden="true" data-id="element-1565" data-mp="10tbyn0hxeut8" />
                {formatHours(module.durationHours)}
              </span>
              <span className="text-xs text-gray-500" data-id="element-1566" data-mp="15sf2ihm66uzm">
                {module.levels.map(l => LEVEL_LABELS[l]).join(', ')}
              </span>
            </div>

            {matchedTerms.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-1567" data-mp="3ezs37z805hw8">
                <span className="text-xs text-gray-500" data-id="element-1568" data-mp="3t14gu3zox7ey">Matched:</span>
                {matchedTerms.slice(0, 4).map(term => <span key={term} className="inline-flex rounded bg-[#30718D]/10 px-2 py-1 text-xs font-medium text-[#30718D]" data-id="element-1569" data-mp="3p0c4h1viumeg">
                    {term}
                  </span>)}
              </div>}
          </div>

          <button type="button" onClick={onToggleExpand} aria-expanded={expanded} aria-controls={detailsId} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1570" data-mp="3a3bdj0l5mvh1">
            {expanded ? 'Less' : 'Details'}
            <ChevronDown size={14} className={`transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} data-id="element-1571" data-mp="2q70d00v508ad" />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-1572" data-mp="34yhkyhdxlr63">
        {expanded && <motion.div id={detailsId} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden border-t border-gray-100" data-id="element-1573" data-mp="3502ga982le50">
            <div className="grid gap-6 p-5 sm:grid-cols-2" data-id="element-1574" data-mp="3nx3mtyh9vmb2">
              <div data-id="element-1575" data-mp="3nuhgu3a15euy">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1576" data-mp="1ojzk3whhaz4l">
                  Topics
                </div>
                <ul className="mt-2 space-y-1" data-id="element-1577" data-mp="1tjlwy4teadcv">
                  {module.topics.map(topic => <li key={topic} className="text-sm text-gray-700" data-id="element-1578" data-mp="1m03476ucn5r3">
                      {topic}
                    </li>)}
                </ul>
              </div>
              <div data-id="element-1579" data-mp="1cajlc16mzbw8">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1580" data-mp="1ca01gexfedfk">
                  What you’ll learn
                </div>
                <ul className="mt-2 space-y-1" data-id="element-1581" data-mp="24yp8eul5xcz8">
                  {module.outcomes.map(outcome => <li key={outcome} className="text-sm text-gray-700" data-id="element-1582" data-mp="1zyjf79z8vif1">
                      {outcome}
                    </li>)}
                </ul>
              </div>
              <div className="sm:col-span-2 border-t border-gray-100 pt-4" data-id="element-1583" data-mp="1q155rubqntw8">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1584" data-mp="1q39847tzokue">
                  Why this came up
                </div>
                <p className="mt-1.5 text-sm text-gray-700" data-id="element-1585" data-mp="ekori4bx7jay">
                  {module.relevance}
                </p>
                <p className="mt-2 text-xs text-gray-400" data-id="element-1586" data-mp="32s6o4mteswwp">
                  Skill Module ID: {module.courseId}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </article>;
}